import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Zero-based page indexes to show, with "gap" markers: first, last, and two either side of the current page.
export function pageItems(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
  const wanted = new Set([0, pageCount - 1, page - 1, page, page + 1]);
  if (page <= 3) [1, 2, 3, 4].forEach((i) => wanted.add(i));
  if (page >= pageCount - 4) [pageCount - 5, pageCount - 4, pageCount - 3, pageCount - 2].forEach((i) => wanted.add(i));
  const sorted = [...wanted].filter((i) => i >= 0 && i < pageCount).sort((a, b) => a - b);
  return sorted.flatMap((i, idx) => (idx > 0 && i - sorted[idx - 1] > 1 ? ["gap", i] : [i]));
}

const PageButton = ({ active, disabled, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-current={active ? "page" : undefined}
    className={`flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-40 ${
      active ? "bg-primaryPurple text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

/**
 * Shared list footer: numbered pages on the left; "Showing X–Y of Z" and rows-per-page on the right.
 * `shown` is the number of rows on the current page; changing the page size resets to page 0 in the caller.
 */
export default function PaginationFooter({ shown, totalCount, page, onPageChange, pageSize, onPageSizeChange, extra = null }) {
  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const isLast = page >= pageCount - 1;
  return (
    <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-muted/30 px-4 py-4">
      <div className="flex flex-wrap items-center gap-1">
        {extra}
        <PageButton label="Previous page" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </PageButton>
        {pageItems(page, pageCount).map((item, i) =>
          item === "gap" ? (
            <span key={`gap-${i}`} className="px-0.5 text-xs text-gray-400">…</span>
          ) : (
            <PageButton key={item} active={item === page} label={`Page ${item + 1}`} onClick={() => item !== page && onPageChange(item)}>
              {item + 1}
            </PageButton>
          )
        )}
        <PageButton label="Next page" disabled={isLast} onClick={() => onPageChange(page + 1)}>
          <ChevronRight className="h-3.5 w-3.5" />
        </PageButton>
      </div>
      <div className="flex items-center gap-3">
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          Showing {shown ? page * pageSize + 1 : 0}–{Math.min(page * pageSize + shown, totalCount)} of {totalCount}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Rows</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-8 w-[72px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
